import { forEachStaticRegion, getStaticRegion, type StaticRegionLang } from '@hero/lang';
import type { Language } from '@hero/schema';
import { makeCustomCode } from '../region/customCode';

const normalizeKey = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

let displayNameToCode: Map<string, string> | undefined;

const buildDisplayNameIndex = (): Map<string, string> => {
  const index = new Map<string, string>();
  forEachStaticRegion((customCode, entry) => {
    for (const name of Object.values(entry.displayName)) {
      if (!name?.trim()) continue;
      index.set(normalizeKey(name), customCode);
    }
  });
  return index;
};

const resolveCityCustomCode = (city: string): string | undefined => {
  const trimmed = city.trim();
  if (!trimmed) return undefined;

  const slug = makeCustomCode(trimmed);
  if (getStaticRegion(slug)) return slug;

  if (!displayNameToCode) displayNameToCode = buildDisplayNameIndex();
  return displayNameToCode.get(normalizeKey(trimmed));
};

/**
 * Resolve a free-form city label (Latin, Arabic, saved-place name) to the
 * conversation language using the L1 region static bundle.
 *
 * WhatsApp RTL routes break when city halves mix Latin + Arabic — both
 * confirmation and post-create must land here before {@link formatLaneRoute}.
 */
export const localizeCityLabel = (city: string, language: Language): string => {
  const trimmed = city.trim();
  if (!trimmed) return trimmed;

  // English is a target like any other: a stop geocoded in Arabic lands
  // next to a stop stored in Latin, and "Riyadh → عرعر" is the same
  // half-translated route in an English UI as it is in an Arabic one.
  const lang: StaticRegionLang = language === 'ur' ? 'ur' : language === 'ar' ? 'ar' : 'en';
  const customCode = resolveCityCustomCode(trimmed);
  if (!customCode) return trimmed;

  return getStaticRegion(customCode, lang)?.displayName ?? trimmed;
};
